import { DEFAULT_LOCALE } from '../i18n'

const INTL_LOCALE = {
  'pt-BR': 'pt-BR',
  es: 'es-ES',
  en: 'en-US'
}

export function intlLocaleFor(locale) {
  return INTL_LOCALE[locale] || INTL_LOCALE[DEFAULT_LOCALE]
}

/** Aceita ISO, array [y,m,d], objeto Java LocalDate ou CSV do Jackson. */
export function parseLocalDate(value) {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d] = value
    const dt = new Date(Number(y), Number(m) - 1, Number(d))
    return Number.isNaN(dt.getTime()) ? null : dt
  }
  if (typeof value === 'object') {
    if (value.year != null && value.day != null) {
      const mo = value.monthValue ?? value.month ?? 1
      const dt = new Date(value.year, Number(mo) - 1, value.day)
      return Number.isNaN(dt.getTime()) ? null : dt
    }
  }
  const s = String(value).trim()
  const br = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (br) {
    const dt = new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]))
    return Number.isNaN(dt.getTime()) ? null : dt
  }
  const csv = s.match(/^(\d{4})\s*,\s*(\d{1,2})\s*,\s*(\d{1,2})/)
  if (csv) {
    const dt = new Date(Number(csv[1]), Number(csv[2]) - 1, Number(csv[3]))
    return Number.isNaN(dt.getTime()) ? null : dt
  }
  const iso = s.split('T')[0]
  const parts = iso.split('-')
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10)
    const m = parseInt(parts[1], 10)
    const d = parseInt(parts[2], 10)
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const dt = new Date(y, m - 1, d)
      return Number.isNaN(dt.getTime()) ? null : dt
    }
  }
  const dt = new Date(s)
  return Number.isNaN(dt.getTime()) ? null : dt
}

export function formatDate(value, locale = DEFAULT_LOCALE, options = {}) {
  if (!value) return '-'
  const date = value instanceof Date ? value : parseLocalDate(value) ?? new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString(intlLocaleFor(locale), options)
}

/**
 * LocalDateTime / Instant do backend Java (array Jackson, ISO, epoch, objeto).
 * @returns {Date|null}
 */
export function parseBackendDateTime(value) {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'number') {
    const ts = value > 1e12 ? value : value * 1000
    const fromTs = new Date(ts)
    return Number.isNaN(fromTs.getTime()) ? null : fromTs
  }
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nanos = 0] = value
    if (!year || !month || !day) return null
    const ms = Math.floor(Number(nanos || 0) / 1000000)
    const fromArray = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      ms
    )
    return Number.isNaN(fromArray.getTime()) ? null : fromArray
  }
  if (typeof value === 'object') {
    const y = Number(value.year ?? value.ano)
    const m = Number(value.monthValue ?? value.month ?? value.mes)
    const d = Number(value.dayOfMonth ?? value.day ?? value.dia)
    const h = Number(value.hour ?? value.hora ?? 0)
    const mi = Number(value.minute ?? value.minuto ?? 0)
    const s = Number(value.second ?? value.segundo ?? 0)
    const nano = Number(value.nano ?? value.nanosecond ?? 0)
    if (y && m && d) {
      const ms = Math.floor(nano / 1000000)
      const fromObj = new Date(y, m - 1, d, h, mi, s, ms)
      return Number.isNaN(fromObj.getTime()) ? null : fromObj
    }
  }
  const raw = String(value).trim()
  if (!raw) return null
  if (/^\d{10,13}$/.test(raw)) {
    const num = Number(raw)
    const ts = raw.length >= 13 ? num : num * 1000
    const fromRawTs = new Date(ts)
    if (!Number.isNaN(fromRawTs.getTime())) return fromRawTs
  }
  let d = new Date(raw)
  if (!Number.isNaN(d.getTime())) return d
  const normalized = raw.replace(' ', 'T')
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/
  )
  if (!match) return null
  const [, year, month, day, hour, minute, second = '0', fraction = '0'] = match
  const ms = Number((fraction + '000').slice(0, 3))
  d = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    ms
  )
  return Number.isNaN(d.getTime()) ? null : d
}

/** Data e hora legíveis a partir de LocalDateTime do backend. */
export function formatBackendDateTime(value, locale = DEFAULT_LOCALE) {
  const d = parseBackendDateTime(value)
  if (!d) return ''
  return d.toLocaleString(intlLocaleFor(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/** dd/MM/yyyy a partir de LocalDate / ISO / array Jackson. */
export function formatLocalDate(value, locale = DEFAULT_LOCALE) {
  const dt = parseLocalDate(value)
  if (!dt) return ''
  return dt.toLocaleDateString(intlLocaleFor(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/** overdue | near (≤ nearDays) | ok | none */
export function prazoDeadlineStatus(value, nearDays = 3) {
  const dataPrazo = parseLocalDate(value)
  if (!dataPrazo) return 'none'
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  dataPrazo.setHours(0, 0, 0, 0)
  const diffDias = Math.round((dataPrazo.getTime() - hoje.getTime()) / 86400000)
  if (diffDias < 0) return 'overdue'
  if (diffDias <= nearDays) return 'near'
  return 'ok'
}

export function formatCurrency(value, locale = DEFAULT_LOCALE, currency = 'BRL') {
  const numberValue = Number(value ?? 0)
  return new Intl.NumberFormat(intlLocaleFor(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numberValue)
}

export function formatNumber(value, locale = DEFAULT_LOCALE, options = {}) {
  const numberValue = Number(value ?? 0)
  return new Intl.NumberFormat(intlLocaleFor(locale), options).format(numberValue)
}

/** Valor monetário sem símbolo de moeda (uso com ícone universal de dinheiro). */
export function formatMoneyAmount(value, locale = DEFAULT_LOCALE) {
  const numberValue = Number(value ?? 0)
  if (!Number.isFinite(numberValue)) {
    return formatMoneyAmount(0, locale)
  }
  return new Intl.NumberFormat(intlLocaleFor(locale), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numberValue)
}
